import { useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { LocaleProvider, useLocale } from '@/i18n/LocaleProvider';
import { bootstrapAppAnalytics } from '@/lib/analytics';
import { isAgritourPublicSlice } from '@/lib/public-slice';
import AppLayout from '@/components/AppLayout';
import GuardedRoute from '@/components/GuardedRoute';
import { WizardSessionProvider } from '@/context/WizardSessionContext';
import LandingScreen from '@/screens/Landing/LandingScreen';
import QuestionnaireScreen from '@/screens/Questionnaire/QuestionnaireScreen';
import MapScreen from '@/screens/Map/MapScreen';
import AssessmentScreen from '@/screens/Assessment/AssessmentScreen';
import PlanningInfoScreen from '@/screens/PlanningInfo/PlanningInfoScreen';
import PlanningInfoPrintScreen from '@/screens/PlanningInfo/PlanningInfoPrintScreen';
import EntrepreneursMapScreen from '@/screens/TourismMap/EntrepreneursMapScreen';
import TourismMapScreen from '@/screens/TourismMap/TourismMapScreen';
import AgriTourScreen from '@/screens/AgriTour/AgriTourScreen';
import AgriTourEntrepreneursMapScreen from '@/screens/AgriTour/AgriTourEntrepreneursMapScreen';
import UnavailableScreen from '@/screens/Unavailable/UnavailableScreen';
import { isAgriTourEntrepreneursMapAllowed } from '@/lib/agritour-entrepreneurs-map-access';

function AnalyticsBootstrap() {
  const { language } = useLocale();

  useEffect(() => {
    void bootstrapAppAnalytics({ locale: language, sessionKey: language });
  }, [language]);

  return null;
}

function PortalQuestionnaireRoute() {
  return (
    <WizardSessionProvider>
      <QuestionnaireScreen />
    </WizardSessionProvider>
  );
}

function AgritourQuestionnaireRoute() {
  return <AgriTourScreen />;
}

function AgriTourEntrepreneursMapRoute() {
  if (!isAgriTourEntrepreneursMapAllowed()) {
    return <UnavailableScreen />;
  }
  return <AgriTourEntrepreneursMapScreen />;
}

export default function App() {
  const agritourSlice = isAgritourPublicSlice();

  return (
    <BrowserRouter>
      <LocaleProvider>
        <AnalyticsBootstrap />
        <Routes>
          <Route
            path="/questionnaire"
            element={
              agritourSlice ? <AgritourQuestionnaireRoute /> : <PortalQuestionnaireRoute />
            }
          />
          <Route
            path="/agritour"
            element={
              agritourSlice ? (
                <Navigate to="/questionnaire" replace />
              ) : (
                <AgriTourScreen />
              )
            }
          />
          <Route
            path="/questionnaire/entrepreneurs-map"
            element={<AgriTourEntrepreneursMapRoute />}
          />
          <Route
            path="/agritour/entrepreneurs-map"
            element={<Navigate to="/questionnaire/entrepreneurs-map" replace />}
          />

          {agritourSlice ? (
            <>
              <Route path="/" element={<Navigate to="/questionnaire" replace />} />
              <Route path="/wizard" element={<Navigate to="/questionnaire" replace />} />
              <Route path="*" element={<UnavailableScreen />} />
            </>
          ) : (
            <>
              <Route element={<AppLayout />}>
                <Route
                  path="/"
                  element={
                    <GuardedRoute>
                      <LandingScreen />
                    </GuardedRoute>
                  }
                />
                <Route path="/wizard" element={<Navigate to="/questionnaire" replace />} />
                <Route
                  path="/internal/portal/questionnaire"
                  element={
                    <GuardedRoute>
                      <PortalQuestionnaireRoute />
                    </GuardedRoute>
                  }
                />
                <Route
                  path="/map"
                  element={
                    <GuardedRoute>
                      <WizardSessionProvider>
                        <MapScreen />
                      </WizardSessionProvider>
                    </GuardedRoute>
                  }
                />
                <Route
                  path="/assessment"
                  element={
                    <GuardedRoute>
                      <WizardSessionProvider>
                        <AssessmentScreen />
                      </WizardSessionProvider>
                    </GuardedRoute>
                  }
                />
                <Route
                  path="/planning-info"
                  element={
                    <GuardedRoute>
                      <WizardSessionProvider>
                        <PlanningInfoScreen />
                      </WizardSessionProvider>
                    </GuardedRoute>
                  }
                />
              </Route>

              <Route
                path="/planning-info/print/:sessionId"
                element={
                  <GuardedRoute>
                    <PlanningInfoPrintScreen />
                  </GuardedRoute>
                }
              />

              <Route
                path="/board-view/tourism-map"
                element={
                  <GuardedRoute>
                    <TourismMapScreen />
                  </GuardedRoute>
                }
              />
              <Route
                path="/board-view/entrepreneurs-map"
                element={
                  <GuardedRoute>
                    <EntrepreneursMapScreen />
                  </GuardedRoute>
                }
              />

              <Route
                path="*"
                element={
                  <GuardedRoute>
                    <Navigate to="/" replace />
                  </GuardedRoute>
                }
              />
            </>
          )}
        </Routes>
      </LocaleProvider>
    </BrowserRouter>
  );
}
